import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { all_routes } from "../../Router/all_routes";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import {
  Plus,
  PlusCircle,
  ShoppingCart,
  Download,
  Printer,
  Trash2,
  Calendar,
  FileText,
  Save,
  ArrowLeft,
  Edit,
  Settings2,
  Paperclip,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import ProductModal from "../../components/modals/ProductModal";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTable } from "@/components/ui/data-table";
import { TableCard } from "@/components/ui/table-card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { ReactSelect } from "@/components/ui/react-select";
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { eventBus } from "../../core/utils/eventBus";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import MediaLibraryComponent from "../media/MediaLibraryComponent";

const NewPurchase = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const currentStore = useSelector((state) => state.current_store);
  const store_id = currentStore?.id || null;
  const employee_id =
    useSelector((state) => state.user_data).employee?.id || null;

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => permissions.includes(permission);

  const [purchase, setPurchase] = useState({
    store_id: "",
    employee_id: "",
    supplier_id: "",
    tracking_number: "",
    shipping_via: "",
    order_date: new Date().toISOString().split("T")[0],
    due_date: new Date().toISOString().split("T")[0],
    sub_total: 0,
    total_tax: 0,
    discount: 0,
    total_discount: 0,
    total: 0,
    shipping: 0,
    paid: 0,
    status: "pending",
    internal_notes: "",
    public_notes: "",
    attachment: "",
    id: "",
  });

  const [orderItems, setOrderItems] = useState([]);

  // Media Library State
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);

  // Product search state
  const [productSearch, setProductSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [supplierSearchInput, setSupplierSearchInput] = useState("");
  const [supplierSearchTerm, setSupplierSearchTerm] = useState("");
  const [supplierLabels, setSupplierLabels] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(productSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [productSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSupplierSearchTerm(supplierSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [supplierSearchInput]);

  // Item Details Modal State
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [activeRowIndex, setActiveRowIndex] = useState(null);

  // Supplier Modal State
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [supplierFormData, setSupplierFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zip: "",
    status: "active",
  });

  // Queries
  const { data: suppliers = [], isFetching: isSuppliersFetching } = useQuery({
    queryKey: ["suppliers", supplierSearchTerm],
    enabled: hasPermission("supplier read"),
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.PURCHASES.SUPPLIERS, {
        params: {
          search: supplierSearchTerm || undefined,
          perPage: 15,
          per_page: 15,
        },
      });
      return response.data.data || [];
    },
  });

  useEffect(() => {
    if (suppliers.length > 0) {
      setSupplierLabels((prev) => {
        const next = { ...prev };
        suppliers.forEach((s) => {
          next[String(s.id)] = s.name;
        });
        return next;
      });
    }
  }, [suppliers]);

  const {
    data: productsData,
    fetchNextPage: fetchNextProductsPage,
    hasNextPage: hasNextProductsPage,
    isFetchingNextPage: isFetchingNextProductsPage,
    isLoading: isProductsLoading,
  } = useInfiniteQuery({
    queryKey: ["pos-products", debouncedSearch, store_id],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
        params: {
          perPage: 15,
          page: pageParam,
          search: debouncedSearch,
          manage_inventory: true,
          store_id,
        },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const meta = lastPage?.meta;
      if (meta && meta.current_page < meta.last_page) {
        return meta.current_page + 1;
      }
      return undefined;
    },
    staleTime: 60 * 1000 * 5, // 5 minutes
  });

  const [extraProducts, setExtraProducts] = useState([]);

  const products = useMemo(() => {
    const fetched = productsData
      ? productsData.pages.flatMap((page) => page.data || [])
      : [];

    const searchLower = (debouncedSearch || "").toLowerCase().trim();

    // Start with extraProducts (matching search if search is active)
    const filteredExtra = extraProducts.filter((p) => {
      if (!searchLower) return true;
      return (
        p.name?.toLowerCase().includes(searchLower) ||
        p.sku?.toLowerCase().includes(searchLower)
      );
    });

    const combined = [...filteredExtra];

    // Include any products already in orderItems (for displaying selected items properly)
    orderItems.forEach((item) => {
      if (item.product && !combined.some((p) => p.id === item.product.id)) {
        if (
          !searchLower ||
          item.product.name?.toLowerCase().includes(searchLower) ||
          item.product.sku?.toLowerCase().includes(searchLower)
        ) {
          combined.push(item.product);
        }
      }
    });

    // Add fetched products
    fetched.forEach((p) => {
      if (!combined.some((item) => item.id === p.id)) {
        combined.push(p);
      }
    });

    return combined.filter(
      (p) =>
        p.tracking_type !== "none" ||
        extraProducts.some((ep) => ep.id === p.id) ||
        orderItems.some((it) => it.product_id === p.id),
    );
  }, [productsData, extraProducts, orderItems, debouncedSearch]);

  const productsLoading = isProductsLoading || isFetchingNextProductsPage;

  // Mutations
  const addPurchaseMutation = useMutation({
    mutationFn: async (purchaseData) => {
      const response = await apiClient.post(
        API_ENDPOINTS.PURCHASES.ORDERS,
        purchaseData,
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["purchases"] });
      toast.success("Purchase Order Added Successfully");
      setPurchase((prev) => ({ ...prev, id: data.id }));
      eventBus.publish("purchase_order:created", data);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to add purchase order",
      );
    },
  });

  const addSupplierMutation = useMutation({
    mutationFn: async (supplierData) => {
      const response = await apiClient.post(
        API_ENDPOINTS.PURCHASES.SUPPLIERS,
        supplierData,
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      toast.success("Supplier added successfully");
      if (data) {
        setSupplierLabels((prev) => ({
          ...prev,
          [String(data.id)]: data.name,
        }));
        handleOrderChange("supplier_id", String(data.id));
      }
      setShowSupplierModal(false);
      setSupplierFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        country: "",
        zip: "",
        status: "active",
      });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to add supplier");
    },
  });

  const generatePDF = async (purchaseOrder) => {
    setPdfLoading(true);
    toast.info("Generating PDF...");
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.PURCHASE_ORDER_REPORT(purchaseOrder.id),
        {
          headers: {
            Accept: "application/pdf",
          },
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setShowSuccessModal(true);
      setPdfLoading(false);
      toast.success("PDF Generated");
    } catch (error) {
      setPdfLoading(false);
      console.error("Error generating PDF:", error);
      toast.error(error.response?.data?.message || "Failed to generate PDF");
    }
  };

  // PDF-related states
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // PDF handler functions
  const handleDownloadPDF = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `Purchase-Order-${purchase.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("PDF downloaded successfully");
    }
  };

  const handlePrintPDF = () => {
    if (pdfUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Remove the iframe after the print dialog is closed
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
    }
  };

  const handleCloseSuccessModal = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setPdfLoading(false);
    setShowSuccessModal(false);
    // Redirect to edit purchase page
    navigate(all_routes.editPurchase.replace(":id", purchase.id));
  };

  const validateFields = () => {
    if (
      !purchase.supplier_id ||
      !purchase.order_date ||
      !purchase.due_date ||
      !purchase.status
    ) {
      toast.error(
        "Please fill all the required fields (Supplier, Date, Due Date, Status)",
      );
      return false;
    }

    if (orderItems.length === 0) {
      toast.error("Please add at least one product.");
      return false;
    }

    // Check if any item is missing product_id, quantity or cost
    const invalidItems = orderItems.some(
      (item) =>
        !item.product_id ||
        parseFloat(item.ordered_qty || 0) < 1 ||
        !item.unit_cost,
    );
    if (invalidItems) {
      toast.error(
        "Please ensure all products have a valid Quantity (min 1) and Cost Price.",
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    // Prevent duplicate submissions
    if (
      addPurchaseMutation.isPending ||
      addPurchaseMutation.isSuccess ||
      pdfLoading
    ) {
      return;
    }

    if (!validateFields()) {
      return;
    }

    const purchaseData = {
      ...purchase,
      store_id,
      employee_id,
      shipping: parseFloat(purchase.shipping || 0),
      discount: parseFloat(purchase.discount || 0),
      sub_total: parseFloat(purchase.sub_total || 0),
      total_tax: parseFloat(purchase.total_tax || 0),
      total: parseFloat(purchase.total || 0),
      items: orderItems.map((item) => ({
        product_id: item.product_id,
        store_id,
        ordered_qty: parseFloat(item.ordered_qty || 0),
        received_qty: parseFloat(item.received_qty || 0),
        unit_cost: parseFloat(item.unit_cost || 0),
        unit_sell_price: parseFloat(item.unit_sell_price || 0),
        unit_sell_price_min: parseFloat(item.unit_sell_price_min || 0),
        unit_sell_price_max: parseFloat(item.unit_sell_price_max || 0),
        tax: parseFloat(item.tax || 0),
        tax_class: item.tax_class || "NA",
        tax_percentage: parseFloat(item.tax_percentage || 0),
        tax_method: item.tax_method || "exclusive",
        discount_type: item.discount_type || "fixed",
        discount_value: parseFloat(item.discount_value || 0),
        discount_amount: parseFloat(item.discount_amount || 0),
        is_foc: item.is_foc || false,
        total: parseFloat(item.total || 0),
        public_notes: item.public_notes || "",
        internal_notes: item.internal_notes || "",
        meta: item.meta || [],
      })),
    };

    addPurchaseMutation.mutate(purchaseData);
  };

  const handleCreateSupplier = async () => {
    if (!supplierFormData.name) {
      toast.error("Supplier name is required!");
      return;
    }
    addSupplierMutation.mutate(supplierFormData);
  };

  const handleMediaSelect = (selectedId, selectedObject) => {
    if (selectedObject && selectedObject.url) {
      handleOrderChange("attachment", selectedObject.url);
      setShowMediaLibrary(false);
    }
  };

  const handleOrderChange = (key, value) => {
    setPurchase({
      ...purchase,
      [key]: value,
    });
  };

  const orderItemChange = (index, key, value, productObj = null) => {
    const newItems = [...orderItems];
    newItems[index][key] = value;

    // If product changes, load its cost price
    if (key === "product_id" && value) {
      const selectedProduct = productObj || products.find((p) => p.id === value);
      if (selectedProduct) {
        newItems[index].unit_cost = selectedProduct.cost || 0;
        newItems[index].product = selectedProduct;
        // Also update sell prices if they are tracking cost
        newItems[index].unit_sell_price = selectedProduct.cost || 0;
        newItems[index].unit_sell_price_min = selectedProduct.cost || 0;
        newItems[index].unit_sell_price_max = selectedProduct.cost || 0;
      }
    }

    // If cost price changes, update all other prices
    if (key === "unit_cost") {
      newItems[index].unit_sell_price = value;
      newItems[index].unit_sell_price_min = value;
      newItems[index].unit_sell_price_max = value;
    }

    // Auto-calculate total and tax
    const item = newItems[index];
    const qty = parseFloat(item.ordered_qty || 0);
    const cost = parseFloat(item.unit_cost || 0);
    const taxPercentage = parseFloat(item.tax_percentage || 0);

    const subtotalBeforeDiscount = qty * cost;
    let discountAmount = 0;

    if (item.is_foc) {
      discountAmount = subtotalBeforeDiscount;
    } else {
      const dValue = parseFloat(item.discount_value || 0);
      if (item.discount_type === "percentage") {
        discountAmount = subtotalBeforeDiscount * (dValue / 100);
      } else {
        discountAmount = dValue;
      }
    }

    newItems[index].discount_amount = discountAmount;

    const subtotalAfterDiscount = subtotalBeforeDiscount - discountAmount;
    let taxAmount = 0;
    let total = subtotalAfterDiscount;

    if (item.tax_method === "exclusive") {
      taxAmount = subtotalAfterDiscount * (taxPercentage / 100);
      total = subtotalAfterDiscount + taxAmount;
    } else if (item.tax_method === "inclusive") {
      // Price already includes tax - do not extract; align with sales tax behaviour
      taxAmount = 0;
      total = subtotalAfterDiscount;
    }

    newItems[index].tax = taxAmount;
    newItems[index].total = total;

    setOrderItems(newItems);
  };

  const addNewItem = () => {
    if (orderItems.length >= 25) {
      toast.error("You cannot add more than 25 items.");
      return;
    }
    setOrderItems([
      ...orderItems,
      {
        product_id: "",
        product: null,
        ordered_qty: 1,
        received_qty: 0,
        unit_cost: 0,
        unit_sell_price: 0,
        unit_sell_price_min: 0,
        unit_sell_price_max: 0,
        tax: 0,
        tax_class: currentStore?.default_tax_class || "NA",
        tax_percentage: parseFloat(currentStore?.tax_percentage || 0),
        tax_method: "exclusive",
        is_foc: false,
        discount_type: "fixed",
        discount_value: 0,
        discount_amount: 0,
        total: 0,
        public_notes: "",
        internal_notes: "",
      },
    ]);
  };

  const removeItem = (index) => {
    const newItems = [...orderItems];
    newItems.splice(index, 1);
    setOrderItems(newItems);
  };

  const calculateTotalAmount = () => {
    let orderSubTotal = 0;
    let orderTotalTax = 0;
    let orderTotalDiscount = 0;
    let itemsTotal = 0;

    orderItems.forEach((item) => {
      if (item) {
        const qty = parseFloat(item.ordered_qty || 0);
        const cost = parseFloat(item.unit_cost || 0);
        const discountAmount = parseFloat(item.discount_amount || 0);
        const isFoc = item.is_foc || false;

        // FOC items are free - exclude their cost from subtotal and discount
        if (!isFoc) {
          orderSubTotal += qty * cost;
          orderTotalDiscount += discountAmount;
        }
        orderTotalTax += parseFloat(item.tax || 0);
        itemsTotal += parseFloat(item.total || 0);
      }
    });

    const shippingAmount = purchase.shipping
      ? parseFloat(purchase.shipping)
      : 0;

    const grandTotal = itemsTotal + shippingAmount;

    if (
      purchase.sub_total !== orderSubTotal ||
      purchase.total_tax !== orderTotalTax ||
      purchase.discount !== orderTotalDiscount ||
      purchase.total_discount !== orderTotalDiscount ||
      purchase.total !== grandTotal
    ) {
      setPurchase((prev) => ({
        ...prev,
        sub_total: orderSubTotal,
        total_tax: orderTotalTax,
        discount: orderTotalDiscount,
        total_discount: orderTotalDiscount,
        total: grandTotal,
      }));
    }
  };

  useEffect(() => {
    calculateTotalAmount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderItems, purchase.shipping]); // Removed purchase.discount from dependencies as it's now calculated from items

  const generateTrackingNumber = () => {
    return "TRK" + Math.floor(100000 + Math.random() * 900000).toString();
  };

  useEffect(() => {
    setPurchase((prev) => ({
      ...prev,
      tracking_number: generateTrackingNumber(),
    }));
  }, []);

  useEffect(() => {
    const unsub = eventBus.subscribe("purchase_order:created", generatePDF);
    return () => unsub();
  }, []);

  const itemColumns = useMemo(
    () => [
      {
        id: "index",
        header: "#",
        cell: ({ row }) => <span className="text-sm">{row.index + 1}</span>,
      },
      {
        accessorKey: "product_id",
        header: "Product",
        cell: ({ row, table }) => {
          const index = row.index;
          const item = row.original;
          const {
            products = [],
            productsLoading = false,
            setProductSearch = () => { },
            orderItemChange = () => { },
            setShowProductModal = () => { },
            setActiveRowIndex = () => { },
            hasNextProductsPage = false,
            fetchNextProductsPage = () => { },
          } = table.options.meta || {};

          return (
            <div className="flex gap-2">
              <div className="flex-grow">
                <ReactSelect
                  isLoading={productsLoading}
                  onInputChange={(val) => setProductSearch(val)}
                  onMenuScrollToBottom={() => {
                    if (hasNextProductsPage) fetchNextProductsPage();
                  }}
                  filterOption={() => true}
                  value={(() => {
                    if (!item.product_id) return null;
                    const p =
                      item.product ||
                      products.find((p) => p.id === item.product_id);
                    const label = p
                      ? `${p.name}${p.sku ? ` (SKU: ${p.sku})` : ""}`
                      : "Select Product";
                    return { value: item.product_id, label };
                  })()}
                  onChange={(selectedOption) => {
                    orderItemChange(
                      index,
                      "product_id",
                      selectedOption ? selectedOption.value : "",
                    );
                  }}
                  options={(() => {
                    const opts = products.map((product) => ({
                      value: product.id,
                      label: `${product.name}${product.sku ? ` (SKU: ${product.sku})` : ""}`,
                    }));
                    if (
                      item.product &&
                      !opts.some((opt) => opt.value === item.product.id)
                    ) {
                      opts.unshift({
                        value: item.product.id,
                        label: `${item.product.name}${item.product.sku ? ` (SKU: ${item.product.sku})` : ""}`,
                      });
                    }
                    return opts;
                  })()}
                  placeholder="Select Product..."
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  className="flex-grow"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={() => {
                  setActiveRowIndex(index);
                  setShowProductModal(true);
                }}
                title="Add New Product"
              >
                <PlusCircle className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
      {
        accessorKey: "ordered_qty",
        header: "Qty",
        cell: ({ row, table }) => {
          const index = row.index;
          const item = row.original;
          const { orderItemChange = () => { } } = table.options.meta || {};
          return (
            <Input
              type="number"
              min="1"
              value={item.ordered_qty}
              onChange={(e) =>
                orderItemChange(index, "ordered_qty", e.target.value)
              }
              className="h-9"
            />
          );
        },
      },
      {
        accessorKey: "unit_cost",
        header: "Cost",
        cell: ({ row, table }) => {
          const index = row.index;
          const item = row.original;
          const { orderItemChange = () => { } } = table.options.meta || {};
          return (
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground text-xs">
                {currencySymbol}
              </span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={item.unit_cost}
                onChange={(e) =>
                  orderItemChange(index, "unit_cost", e.target.value)
                }
                className="h-9"
              />
            </div>
          );
        },
      },
      {
        accessorKey: "tax_method",
        header: "Tax Method",
        cell: ({ row, table }) => {
          const index = row.index;
          const item = row.original;
          const { orderItemChange = () => { } } = table.options.meta || {};
          return (
            <ReactSelect
              options={[
                { value: "exclusive", label: "Exclusive" },
                { value: "inclusive", label: "Inclusive" },
              ]}
              value={[
                { value: "exclusive", label: "Exclusive" },
                { value: "inclusive", label: "Inclusive" },
              ].find((opt) => opt.value === item.tax_method)}
              onChange={(opt) =>
                orderItemChange(index, "tax_method", opt?.value)
              }
              className="w-full"
            />
          );
        },
      },
      {
        accessorKey: "tax",
        header: "Tax",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="space-y-1 text-center">
              <span className="text-sm font-medium">
                {currencySymbol}
                {parseFloat(item.tax || 0).toFixed(2)}
              </span>
              <div className="text-[10px] text-muted-foreground">
                {item.tax_class} ({item.tax_percentage}%)
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "total",
        header: "Total",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="text-center">
              <span className="font-bold text-primary">
                {currencySymbol}
                {parseFloat(item.total || 0).toFixed(2)}
              </span>
              {item.is_foc && (
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  Free of Charge
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: () => <div className="text-right">Actions</div>,
        cell: ({ row, table }) => {
          const index = row.index;
          const {
            setEditingItemIndex = () => { },
            setShowItemModal = () => { },
            removeItem = () => { },
            hasPermission = () => false,
          } = table.options.meta || {};
          return (
            <div className="flex justify-end gap-1">
              {hasPermission("purchase order item update") && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                  onClick={() => {
                    setEditingItemIndex(index);
                    setShowItemModal(true);
                  }}
                  type="button"
                >
                  <Settings2 className="h-4 w-4" />
                </Button>
              )}
              {hasPermission("purchase order item delete") && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => removeItem(index)}
                  type="button"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [currencySymbol],
  );

  const handleProductCreated = (newProduct) => {
    // 1. Immediately add to extraProducts so it's guaranteed in options & products list
    setExtraProducts((prev) => [
      newProduct,
      ...prev.filter((p) => p.id !== newProduct.id),
    ]);

    // Clear product search input so user sees full product list
    setProductSearch("");

    // 2. Select the created product in the active row immediately
    let targetIndex = activeRowIndex;
    if (
      targetIndex === null ||
      targetIndex === undefined ||
      targetIndex >= orderItems.length
    ) {
      const emptyIdx = orderItems.findIndex((it) => !it.product_id);
      if (emptyIdx !== -1) {
        targetIndex = emptyIdx;
      } else if (orderItems.length > 0) {
        targetIndex = orderItems.length - 1;
      }
    }

    if (
      targetIndex !== null &&
      targetIndex !== undefined &&
      targetIndex >= 0 &&
      targetIndex < orderItems.length
    ) {
      orderItemChange(targetIndex, "product_id", newProduct.id, newProduct);
    } else {
      const cost = parseFloat(newProduct.cost) || 0;
      const newItem = {
        product_id: newProduct.id,
        product: newProduct,
        ordered_qty: 1,
        received_qty: 0,
        unit_cost: cost,
        unit_sell_price: cost,
        unit_sell_price_min: cost,
        unit_sell_price_max: cost,
        tax: 0,
        tax_class: currentStore?.default_tax_class || "NA",
        tax_percentage: parseFloat(currentStore?.tax_percentage || 0),
        tax_method: "exclusive",
        is_foc: false,
        discount_type: "fixed",
        discount_value: 0,
        discount_amount: 0,
        total: cost,
        public_notes: "",
        internal_notes: "",
      };
      setOrderItems((prev) => [...prev, newItem]);
    }

    // 3. Invalidate product queries to sync fresh data from the server
    queryClient.invalidateQueries({ queryKey: ["pos-products"] });
    queryClient.invalidateQueries({ queryKey: ["all-pos-products"] });
    queryClient.invalidateQueries({ queryKey: ["products"] });
    queryClient.invalidateQueries({ queryKey: ["posProducts"] });
  };

  return (
    <div className="page-wrapper">
      <div className="content p-6 max-w-[100vw] overflow-x-hidden">
        <PageHeader>
          <PageHeaderHeading
            title="Create Purchase Order"
            subtitle="Add a new purchase order to your inventory"
          />
          <PageHeaderActions>
            <Button
              variant="outline"
              onClick={() => navigate(all_routes.purchaselist)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to List
            </Button>
            {hasPermission("purchase order create") && (
              <Button
                onClick={handleSubmit}
                disabled={
                  addPurchaseMutation.isPending ||
                  addPurchaseMutation.isSuccess ||
                  pdfLoading
                }
              >
                <Save className="mr-2 h-4 w-4" />
                {addPurchaseMutation.isPending || pdfLoading
                  ? "Saving..."
                  : "Save Order"}
              </Button>
            )}
          </PageHeaderActions>
        </PageHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Order Details Card */}
          <Card>
            <CardHeader>
              <CardTitle>Order Information</CardTitle>
              <CardDescription>
                Basic details about the purchase order.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Supplier Selection */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label>
                      Supplier <span className="text-red-500">*</span>
                    </Label>
                    {hasPermission("supplier create") && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 text-primary"
                        onClick={() => setShowSupplierModal(true)}
                      >
                        <PlusCircle className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <ReactSelect
                    onInputChange={(value) => setSupplierSearchInput(value)}
                    filterOption={() => true}
                    options={suppliers.map((supplier) => ({
                      value: String(supplier.id),
                      label: supplier.name,
                    }))}
                    value={
                      purchase.supplier_id
                        ? {
                          value: String(purchase.supplier_id),
                          label:
                            supplierLabels[String(purchase.supplier_id)] ||
                            suppliers.find(
                              (s) =>
                                String(s.id) === String(purchase.supplier_id),
                            )?.name ||
                            "Select Supplier",
                        }
                        : null
                    }
                    onChange={(opt) =>
                      handleOrderChange("supplier_id", opt ? opt.value : "")
                    }
                    placeholder="Search Supplier"
                    isLoading={isSuppliersFetching}
                    isClearable
                  />
                </div>

                {/* Status */}
                <div className="space-y-2">
                  <Label>
                    Status <span className="text-red-500">*</span>
                  </Label>
                  <ReactSelect
                    options={[
                      { value: "draft", label: "Draft" },
                      { value: "pending", label: "Pending" },
                      { value: "approved", label: "Approved" },
                      { value: "sent", label: "Sent" },
                      { value: "partial", label: "Partial" },
                      { value: "completed", label: "Completed" },
                      { value: "canceled", label: "Canceled" },
                    ]}
                    value={
                      purchase.status
                        ? [
                          { value: "draft", label: "Draft" },
                          { value: "pending", label: "Pending" },
                          { value: "approved", label: "Approved" },
                          { value: "sent", label: "Sent" },
                          { value: "partial", label: "Partial" },
                          { value: "completed", label: "Completed" },
                          { value: "canceled", label: "Canceled" },
                        ].find((opt) => opt.value === purchase.status)
                        : null
                    }
                    onChange={(opt) =>
                      handleOrderChange("status", opt ? opt.value : "")
                    }
                    placeholder="Select Status"
                  />
                </div>

                {/* Order Date */}
                <div className="space-y-2">
                  <Label>
                    Order Date <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      type="date"
                      value={purchase.order_date}
                      onChange={(e) =>
                        handleOrderChange("order_date", e.target.value)
                      }
                      required
                    />
                  </div>
                </div>

                {/* Due Date */}
                <div className="space-y-2">
                  <Label>
                    Due Date <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      type="date"
                      value={purchase.due_date}
                      onChange={(e) =>
                        handleOrderChange("due_date", e.target.value)
                      }
                      required
                    />
                  </div>
                </div>

                {/* Tracking Number */}
                <div className="space-y-2">
                  <Label>Tracking Number</Label>
                  <Input
                    value={purchase.tracking_number}
                    onChange={(e) =>
                      handleOrderChange("tracking_number", e.target.value)
                    }
                  />
                </div>

                {/* Shipping Via */}
                <div className="space-y-2">
                  <Label>Shipping Via</Label>
                  <Input
                    value={purchase.shipping_via}
                    onChange={(e) =>
                      handleOrderChange("shipping_via", e.target.value)
                    }
                  />
                </div>

                {/* Attachment */}
                <div className="space-y-2">
                  <Label>Attachment</Label>
                  <div className="flex gap-2">
                    <Input
                      value={purchase.attachment}
                      readOnly
                      placeholder="No file selected..."
                      className="bg-muted cursor-default"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setShowMediaLibrary(true)}
                    >
                      <Paperclip className="h-4 w-4" />
                    </Button>
                    {purchase.attachment && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOrderChange("attachment", "")}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                {/* Internal Notes */}
                <div className="space-y-2">
                  <Label>Internal Notes</Label>
                  <Textarea
                    value={purchase.internal_notes}
                    onChange={(e) =>
                      handleOrderChange("internal_notes", e.target.value)
                    }
                    placeholder="Notes for internal use..."
                    className="min-h-[80px]"
                  />
                </div>
                {/* Public Notes */}
                <div className="space-y-2">
                  <Label>Public Notes</Label>
                  <Textarea
                    value={purchase.public_notes}
                    onChange={(e) =>
                      handleOrderChange("public_notes", e.target.value)
                    }
                    placeholder="Notes visible on the order..."
                    className="min-h-[80px]"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Products Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle>Order Items</CardTitle>
                <CardDescription>Add products to this order.</CardDescription>
              </div>
              {hasPermission("purchase order item create") && (
                <Button onClick={addNewItem} size="sm" type="button" disabled={orderItems.length >= 25}>
                  <Plus className="mr-2 h-4 w-4" /> Add Product
                </Button>
              )}
            </CardHeader>
            <CardContent className="px-6">
              <TableCard className="rounded-none border-x-0 border-b-0 shadow-none">
                <DataTable
                  columns={itemColumns}
                  data={orderItems}
                  isLoading={false}
                  enableRowSelection={false}
                  showPagination={false}
                  meta={{
                    products,
                    productsLoading,
                    setProductSearch,
                    orderItemChange,
                    setEditingItemIndex,
                    setShowItemModal,
                    removeItem,
                    setShowProductModal,
                    setActiveRowIndex,
                    hasNextProductsPage,
                    fetchNextProductsPage,
                    hasPermission,
                  }}
                />
              </TableCard>
            </CardContent>
            {orderItems.length > 0 && (
              <CardFooter className="flex flex-col items-end py-4 border-t gap-4">
                <div className="w-full max-w-sm space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Sub Total:</span>
                    <span className="font-medium">
                      {currencySymbol}
                      {parseFloat(purchase.sub_total || 0).toFixed(2)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Order Tax:</span>
                    <span className="font-medium">
                      {currencySymbol}
                      {parseFloat(purchase.total_tax || 0).toFixed(2)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground pt-2">
                      Order Discount:
                    </span>
                    <span className="font-medium">
                      {currencySymbol}
                      {parseFloat(purchase.discount || 0).toFixed(2)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground pt-2">
                      Shipping Cost:
                    </span>
                    <div className="w-32">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={purchase.shipping}
                        onChange={(e) =>
                          handleOrderChange("shipping", e.target.value)
                        }
                        className="h-8 text-right"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center text-sm border-t pt-2">
                    <span className="font-bold">Grand Total:</span>
                    <span className="font-bold text-lg">
                      {currencySymbol}
                      {parseFloat(purchase.total || 0).toFixed(2)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-sm border-t pt-2">
                    <span className="text-muted-foreground pt-2">
                      Amount Paid:
                    </span>
                    <div className="w-32">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={purchase.paid}
                        onChange={(e) =>
                          handleOrderChange("paid", e.target.value)
                        }
                        className="h-8 text-right"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center text-sm pt-2">
                    <span className="font-semibold text-muted-foreground">
                      Balance Due:
                    </span>
                    <span className="font-semibold text-red-600">
                      {currencySymbol}
                      {(
                        parseFloat(purchase.total || 0) -
                        parseFloat(purchase.paid || 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              </CardFooter>
            )}{" "}
          </Card>
        </form>

        {/* Add Supplier Dialog */}
        <Dialog open={showSupplierModal} onOpenChange={setShowSupplierModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Supplier</DialogTitle>
              <DialogDescription>Enter supplier details.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>
                    Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={supplierFormData.name}
                    onChange={(e) =>
                      setSupplierFormData({
                        ...supplierFormData,
                        name: e.target.value,
                      })
                    }
                    placeholder="Supplier Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={supplierFormData.email}
                    onChange={(e) =>
                      setSupplierFormData({
                        ...supplierFormData,
                        email: e.target.value,
                      })
                    }
                    placeholder="Email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={supplierFormData.phone}
                    onChange={(e) =>
                      setSupplierFormData({
                        ...supplierFormData,
                        phone: e.target.value,
                      })
                    }
                    placeholder="Phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={supplierFormData.city}
                    onChange={(e) =>
                      setSupplierFormData({
                        ...supplierFormData,
                        city: e.target.value,
                      })
                    }
                    placeholder="City"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowSupplierModal(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateSupplier}>Save Supplier</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Configure Item Modal */}
        <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Configure Item</DialogTitle>
              <DialogDescription>
                Adjust quantity, cost, taxes and notes for this item.
              </DialogDescription>
            </DialogHeader>
            {editingItemIndex !== null && orderItems[editingItemIndex] && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                <div className="space-y-4 col-span-2">
                  <div className="space-y-2 text-sm">
                    <Label className="text-muted-foreground">Product</Label>
                    <div className="font-semibold text-base py-1 border-b">
                      {orderItems[editingItemIndex].product?.name ||
                        products.find(
                          (p) =>
                            p.id === orderItems[editingItemIndex].product_id,
                        )?.name ||
                        "Select Product"}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={orderItems[editingItemIndex].ordered_qty}
                    onChange={(e) =>
                      orderItemChange(
                        editingItemIndex,
                        "ordered_qty",
                        e.target.value,
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Unit Cost ({currencySymbol})</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={orderItems[editingItemIndex].unit_cost}
                    onChange={(e) =>
                      orderItemChange(
                        editingItemIndex,
                        "unit_cost",
                        e.target.value,
                      )
                    }
                  />
                </div>

                <div className="flex items-center space-x-2 pt-8">
                  <Checkbox
                    id="is_foc"
                    checked={orderItems[editingItemIndex].is_foc}
                    onCheckedChange={(checked) =>
                      orderItemChange(editingItemIndex, "is_foc", checked)
                    }
                  />
                  <Label htmlFor="is_foc" className="cursor-pointer">
                    Free of Charge (FOC)
                  </Label>
                </div>

                {!orderItems[editingItemIndex].is_foc && (
                  <>
                    <div className="space-y-2">
                      <Label>Discount Type</Label>
                      <ReactSelect
                        options={[
                          { value: "fixed", label: "Fixed Amount" },
                          { value: "percentage", label: "Percentage" },
                        ]}
                        value={[
                          { value: "fixed", label: "Fixed Amount" },
                          { value: "percentage", label: "Percentage" },
                        ].find(
                          (opt) =>
                            opt.value ===
                            orderItems[editingItemIndex].discount_type,
                        )}
                        onChange={(opt) =>
                          orderItemChange(
                            editingItemIndex,
                            "discount_type",
                            opt.value,
                          )
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>
                        Discount Value{" "}
                        {orderItems[editingItemIndex].discount_type ===
                          "percentage"
                          ? "(%)"
                          : `(${currencySymbol})`}
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={orderItems[editingItemIndex].discount_value || 0}
                        onChange={(e) =>
                          orderItemChange(
                            editingItemIndex,
                            "discount_value",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>Tax Method</Label>
                  <ReactSelect
                    options={[
                      { value: "exclusive", label: "Exclusive" },
                      { value: "inclusive", label: "Inclusive" },
                    ]}
                    value={[
                      { value: "exclusive", label: "Exclusive" },
                      { value: "inclusive", label: "Inclusive" },
                    ].find(
                      (opt) =>
                        opt.value === orderItems[editingItemIndex].tax_method,
                    )}
                    onChange={(opt) =>
                      orderItemChange(editingItemIndex, "tax_method", opt.value)
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Tax Class</Label>
                  <Input
                    value={orderItems[editingItemIndex].tax_class || ""}
                    onChange={(e) =>
                      orderItemChange(
                        editingItemIndex,
                        "tax_class",
                        e.target.value,
                      )
                    }
                    placeholder="e.g. VAT"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Tax Percentage (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={orderItems[editingItemIndex].tax_percentage || 0}
                    onChange={(e) =>
                      orderItemChange(
                        editingItemIndex,
                        "tax_percentage",
                        e.target.value,
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Unit Sell Price ({currencySymbol})</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={orderItems[editingItemIndex].unit_sell_price || 0}
                    onChange={(e) =>
                      orderItemChange(
                        editingItemIndex,
                        "unit_sell_price",
                        e.target.value,
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Min Sell Price ({currencySymbol})</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={
                      orderItems[editingItemIndex].unit_sell_price_min || 0
                    }
                    onChange={(e) =>
                      orderItemChange(
                        editingItemIndex,
                        "unit_sell_price_min",
                        e.target.value,
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Max Sell Price ({currencySymbol})</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={
                      orderItems[editingItemIndex].unit_sell_price_max || 0
                    }
                    onChange={(e) =>
                      orderItemChange(
                        editingItemIndex,
                        "unit_sell_price_max",
                        e.target.value,
                      )
                    }
                  />
                </div>

                <div className="space-y-2 col-span-2">
                  <div className="bg-muted/50 p-3 rounded-md grid grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Line Discount</Label>
                      <div className="text-sm font-medium">
                        {currencySymbol}
                        {parseFloat(
                          orderItems[editingItemIndex].discount_amount || 0,
                        ).toFixed(2)}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Calculated Tax</Label>
                      <div className="text-sm font-medium">
                        {currencySymbol}
                        {parseFloat(
                          orderItems[editingItemIndex].tax || 0,
                        ).toFixed(2)}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Line Total</Label>
                      <div className="text-sm font-bold text-primary">
                        {currencySymbol}
                        {parseFloat(
                          orderItems[editingItemIndex].total || 0,
                        ).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Public Notes</Label>
                  <Textarea
                    value={orderItems[editingItemIndex].public_notes || ""}
                    onChange={(e) =>
                      orderItemChange(
                        editingItemIndex,
                        "public_notes",
                        e.target.value,
                      )
                    }
                    placeholder="Notes visible on invoice..."
                    className="min-h-[80px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Internal Notes</Label>
                  <Textarea
                    value={orderItems[editingItemIndex].internal_notes || ""}
                    onChange={(e) =>
                      orderItemChange(
                        editingItemIndex,
                        "internal_notes",
                        e.target.value,
                      )
                    }
                    placeholder="Notes for internal use..."
                    className="min-h-[80px]"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                onClick={() => setShowItemModal(false)}
                className="w-full"
              >
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Success/PDF Modal */}
        <Dialog
          open={showSuccessModal}
          onOpenChange={(isOpen) => {
            if (!isOpen) handleCloseSuccessModal();
          }}
        >
          <DialogContent onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>Purchase Order Created</DialogTitle>
              <DialogDescription>
                Your purchase order has been successfully created.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center justify-center p-6 space-y-4">
              <div className="p-3 bg-green-100 rounded-full">
                <FileText className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-center text-muted-foreground">
                What would you like to do next?
              </p>
              <div className="flex gap-4 w-full justify-center">
                <Button onClick={handleDownloadPDF} variant="outline">
                  <Download className="mr-2 h-4 w-4" /> Download PDF
                </Button>
                <Button onClick={handlePrintPDF} variant="outline">
                  <Printer className="mr-2 h-4 w-4" /> Print
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleCloseSuccessModal} className="w-full">
                Close & View Order
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Media Library Modal */}
        <MediaLibraryComponent
          isOpen={showMediaLibrary}
          onClose={() => setShowMediaLibrary(false)}
          onSelect={handleMediaSelect}
          isMultiple={false}
          selectionMode="single"
        />

        <ProductModal
          open={showProductModal}
          onOpenChange={setShowProductModal}
          defaultTrackingType="standard"
          defaultManageInventory={true}
          onSuccess={handleProductCreated}
        />
      </div>
    </div>
  );
};

export default NewPurchase;
